/**
 * `/$org/spaces`: every space in the organization on one list, beside Home's
 * sidebar, in the Apps page's frame. Each row shows the space's glyph, its
 * name, how many chats it holds and when one last moved; the row opens the
 * space's page (`/$org/spaces/$spaceId`), as the sidebar's rows do. New space
 * is the sidebar's own create flow.
 *
 * Chats are the organization's own (`selectOrgThreads`), counted against the
 * server's assignments; nothing is estimated.
 *
 * @module components/spaces/SpacesPage
 */
import { type HairlineProps, Lockers } from "@lucasmarkes/hairline/react";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, LayersIcon, PlusIcon } from "lucide-react";
import { useMemo, type ComponentType, type ReactNode } from "react";

import { useNowMinute } from "../../hooks/useNowMinute";
import { orgUrlSegment } from "../../orgRouting";
import { useThreadShells } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useActiveOrg, useActiveOrgId } from "../../state/orgs";
import { useOrgSpaces } from "../../state/spaces";
import { selectOrgThreads } from "../home/homeLogic";
import { NewSpacePopover } from "../home/HomeSpaces";
import { SpaceGlyph } from "./SpaceGlyph";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Button } from "../ui/button";
import { HairlineArt } from "../ui/hairline-art";
import { ScrollArea } from "../ui/scroll-area";
import {
  spaceActivityLabel,
  spaceChatsLabel,
  summarizeSpaces,
  type SpaceSummary,
} from "./spacesPageLogic";

const createdOn = (iso: string) => {
  const at = Date.parse(iso);
  return Number.isNaN(at)
    ? null
    : new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

function SpaceRow({
  summary,
  org,
  now,
}: {
  readonly summary: SpaceSummary;
  readonly org: string;
  readonly now: number;
}) {
  const { space, chats, lastActivityMs } = summary;
  const activity = spaceActivityLabel(lastActivityMs, now);
  const created = createdOn(space.createdAt);
  return (
    <li className="group relative flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors hover:bg-muted/50">
      <Link
        to="/$org/spaces/$spaceId"
        params={{ org, spaceId: space.id }}
        data-spaces-row={space.id}
        aria-label={`Open ${space.name}`}
        className="absolute inset-0 rounded-2xl outline-none ring-ring focus-visible:ring-2"
      />
      <SpaceGlyph space={space} className="size-10 rounded-xl text-[15px]" />
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{space.name}</span>
        <p className="truncate text-sm text-muted-foreground">
          {space.client ? <span>Client: {space.client.name ?? "linked organization"} · </span> : null}
          {spaceChatsLabel(chats)}
          {created ? <span className="max-sm:hidden"> · Created {created}</span> : null}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {activity ? (
          <span className="text-xs tabular-nums text-muted-foreground" data-spaces-activity="">
            {activity}
          </span>
        ) : null}
        <ChevronRightIcon className="size-4 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground max-sm:hidden" />
      </div>
    </li>
  );
}

function SpacesNotice({
  title,
  body,
  action,
  art,
}: {
  readonly title: string;
  readonly body: string;
  readonly action?: ReactNode;
  readonly art?: ComponentType<HairlineProps>;
}) {
  return (
    <div
      className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-border/60 px-6 py-16 text-center"
      data-spaces-empty=""
    >
      {art ? (
        <HairlineArt figure={art} className="mb-1 w-48 sm:w-56" />
      ) : (
        <LayersIcon className="size-5 text-muted-foreground/60" />
      )}
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

const NEW_SPACE_TRIGGER = <Button size="sm" className="cursor-pointer rounded-full" data-spaces-new="" />;

export function SpacesPage() {
  const orgId = useActiveOrgId();
  const { available, failed, isPending, refresh, spaces, spaceIdByThreadId } = useOrgSpaces(orgId);
  const activeOrg = useActiveOrg();
  const environmentId = usePrimaryEnvironmentId();
  const threads = useThreadShells();
  const nowMinute = useNowMinute();
  const now = Date.parse(`${nowMinute}:00.000Z`) || Date.now();
  const summaries = useMemo(
    () => summarizeSpaces(spaces, selectOrgThreads(threads, { orgId, environmentId }), spaceIdByThreadId),
    [environmentId, orgId, spaceIdByThreadId, spaces, threads],
  );
  const org = orgId ? orgUrlSegment(orgId) : null;
  const canCreate = org !== null && available && !failed;

  return (
    <ScrollArea className="h-full bg-background text-foreground">
      <div className="mx-auto w-full max-w-4xl px-4 pb-16 pt-14 sm:px-6 sm:pt-8" data-spaces-page="">
        <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-foreground">Spaces</h1>
            <p className="mt-1 max-w-xl text-[13px] text-muted-foreground">
              {activeOrg
                ? `Chats grouped by client, fund or project, shared with everyone in ${activeOrg.name}.`
                : "Chats grouped by client, fund or project, shared with everyone in the organization."}
            </p>
          </div>
          {canCreate ? (
            <NewSpacePopover render={NEW_SPACE_TRIGGER} side="bottom" align="end">
              <PlusIcon />
              New space
            </NewSpacePopover>
          ) : null}
        </header>

        {org === null || isPending ? (
          <ListSkeleton className="mt-6" rows={4} />
        ) : failed ? (
          <SpacesNotice
            title="Spaces could not be loaded"
            body="The workspace server did not answer. Try again in a moment."
            action={
              <Button size="sm" variant="outline" className="cursor-pointer rounded-full" onClick={refresh}>
                Try again
              </Button>
            }
          />
        ) : !available ? (
          <SpacesNotice
            title="Spaces are not available here"
            body="This workspace server does not offer Spaces yet."
          />
        ) : summaries.length === 0 ? (
          <SpacesNotice
            art={Lockers}
            title="No spaces yet"
            body="A space groups chats for a client, a fund or a project. Create one and start chatting in it."
            action={
              <NewSpacePopover render={NEW_SPACE_TRIGGER} side="bottom" align="start">
                <PlusIcon />
                New space
              </NewSpacePopover>
            }
          />
        ) : (
          <section aria-label="All spaces" className="mt-6">
            <div className="border-b border-border/60 pb-2">
              <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
                All spaces
                <span className="ml-2 text-sm font-normal tabular-nums text-muted-foreground">
                  {summaries.length}
                </span>
              </h2>
            </div>
            <ul className="-mx-3 mt-2" data-spaces-list="">
              {summaries.map((summary) => (
                <SpaceRow key={summary.space.id} summary={summary} org={org} now={now} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </ScrollArea>
  );
}
